import { useId } from "@fluentui/react-components";
import { Avatar, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { ContactActions } from "../../helper/ContactActions";
import { IContactData, IResult } from "../../SharedComponents/Interfaces/CustomContact.interfaces";
import { contactListStyles as styles } from "./ContactList.styles";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { useSelector } from "react-redux";
import { selectGraphToken } from "../../slices/Auth.slice";

// Convert name to proper case (e.g., "MHAMPTON" -> "Mhampton")
const toProperCase = (name: string | undefined): string => {
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

interface EngagementContactsSectionProps {
    contactData: IResult;
    accountManager?: string;
    accountManagerAlias?: string;
}

export const EngagementContactsSection = ({ contactData, accountManager, accountManagerAlias }: EngagementContactsSectionProps) => {
    const tableId = useId("engagement-contacts-list");
    const graphToken = useSelector(selectGraphToken);
    const [accountManagerImage, setAccountManagerImage] = useState<string | null>(null);

    // Fetch account manager profile image using the alias
    useEffect(() => {
        const fetchAccountManagerImage = async () => {
            if (!accountManagerAlias || !graphToken) return;

            const email = `${accountManagerAlias}@microsoft.com`;
            try {
                const response = await fetch(
                    `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}/photos/48x48/$value`,
                    {
                        headers: {
                            Authorization: `Bearer ${graphToken}`
                        }
                    }
                );

                if (response.ok) {
                    const blob = await response.blob();
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setAccountManagerImage(reader.result as string);
                    };
                    reader.readAsDataURL(blob);
                }
            } catch {
                // Image fetch failed, will use initials
            }
        };

        fetchAccountManagerImage();
    }, [accountManagerAlias, graphToken]);

    const HandleAPIException = () => (contactData.error && <DisplayAPIsException height={250} />) || "";

    const getNameInitials = (name: string) => {
        const idx = name.lastIndexOf(" ");
        const _name: string = name.charAt(idx > 0 ? idx + 1 : idx);
        return _name.match(/^[a-z]/i) ? _name : "";
    };

    const getAvatarAndFullName = (contact: IContactData) => {
        const displayName = toProperCase(contact.fullName || contact.name);
        return (
            <>
                {contact.imageSrc ? (
                    <Avatar alt={displayName || ""} src={contact.imageSrc} />
                ) : (
                    <Avatar sx={{ bgcolor: "var(--colorBrandBackgroundHover)" }} aria-label="contact" title={displayName}>
                        {displayName?.charAt(0)}
                        {getNameInitials(displayName || "")}
                    </Avatar>
                )}
            </>
        );
    };

    // Flatten all contacts from all groups into a single specialists list
    const allSpecialists = contactData.data?.flatMap((group) => group.data) || [];

    // Loading is handled by parent component (ContactPanel)
    if (contactData.error) {
        return <HandleAPIException />;
    }

    return (
        <>
            <br />
            {/* Account Manager Section */}
            {accountManager && (
                <div>
                    <label className={styles.titleArea}>ACCOUNT MANAGER</label>
                    <Divider orientation="horizontal" />
                    <div className={styles.divTag}></div>
                    <table className={styles.tableBorder}>
                        <tbody className={styles.tableBorder}>
                            <tr className={styles.tableBorder}>
                                <td className={styles.tableBorder}>
                                    {accountManagerImage ? (
                                        <Avatar alt={accountManager} src={accountManagerImage} />
                                    ) : (
                                        <Avatar sx={{ bgcolor: "var(--colorBrandBackgroundHover)" }} aria-label="contact" title={accountManager}>
                                            {accountManager?.charAt(0)}
                                            {getNameInitials(accountManager || "")}
                                        </Avatar>
                                    )}
                                </td>
                                <td className={styles.tableBorder}>
                                    <div className={styles.contactDiv}>
                                        <div className={styles.contactName}>
                                            <label title={accountManager}>
                                                {accountManager}
                                            </label>
                                            <br />
                                            <label className={styles.contactJob} title="Account Manager">
                                                Account Manager
                                            </label>
                                        </div>
                                    </div>
                                </td>
                                <td className={styles.tableBorder}>
                                    {accountManagerAlias && (
                                        <ContactActions email={`${accountManagerAlias}@microsoft.com`} />
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <br />
                </div>
            )}

            {/* Specialists Section - Single header for all specialists */}
            {allSpecialists.length > 0 && (
                <div>
                    <label className={styles.titleArea}>SECURITY SPECIALISTS</label>
                    <Divider orientation="horizontal" />
                    <div className={styles.divTag}></div>
                    {allSpecialists.map((contact, index) => (
                        <table key={`${tableId}-specialist-${index}`} className={styles.tableBorder}>
                            <tbody className={styles.tableBorder}>
                                <tr className={styles.tableBorder}>
                                    <td className={styles.tableBorder}>{getAvatarAndFullName(contact)}</td>
                                    <td className={styles.tableBorder}>
                                        <div className={styles.contactDiv}>
                                            <div className={styles.contactName}>
                                                <label title={toProperCase(contact.fullName || contact.name)}>
                                                    {toProperCase(contact.fullName || contact.name)}
                                                </label>
                                                <br />
                                                <label className={styles.contactJob} title={contact.title}>
                                                    {contact.title}
                                                </label>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={styles.tableBorder}>
                                        <ContactActions email={contact.emailAddress} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    ))}
                    <br />
                </div>
            )}

            {/* Add a separator line before Primary CSAM section */}
            {(accountManager || allSpecialists.length > 0) && (
                <Divider orientation="horizontal" style={{ marginBottom: "10px", marginTop: "10px" }} />
            )}
        </>
    );
};
